import { all, takeLatest, put, call, fork, select } from 'redux-saga/effects';
import {
    getCustomerAddresses,
    postCustomerAddress,
    deleteCustomerAddress,
    deleteAddressWithCreateUpdate,
    updateCustomerAddress,
    setDefaultAddress,
} from 'yoda-interfaces/lib/Account/AccountApi';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import ClientSideLoggerAction from 'yoda-site-components/lib/actions/ClientLoggerAction';
import get from 'lodash/get';
import {
    getAccertifyDeviceInfo,
    logExtendedAnalytics,
} from 'yoda-site-components/lib/sagas/SiteProbingSaga';
import { RESET_QUICKSCREEN_ELIGIBLITY } from 'yoda-account-components/lib/actionTypes/SYFActionTypes';
import { getPlccBannerDetails } from './PaymentMethodsSaga';
import {
    GET_ADDRESSES_SUCCESS,
    GET_ADDRESSES_ERROR,
    UPDATE_ADDRESSES_SUCCESS,
    UPDATE_ADDRESSES_ERROR,
    POST_ADDRESSES_SUCCESS,
    POST_ADDRESSES_ERROR,
    DELETE_ADDRESSES_SUCCESS,
    DELETE_ADDRESSES_ERROR,
    DELETE_ADDRESSES_LOADING,
    DELETE_ADDRESSES_CREATE_SUCCESS,
    GET_ADDRESSES,
    POST_ADDRESSES,
    UPDATE_ADDRESSES,
    DELETE_ADDRESSES,
    DELETE_ADDRESSES_CREATE,
    DELETE_ADDRESSES_UPDATE,
    SET_DEFAULT_ADDRESS,
} from '../actionTypes/AddressActionTypes';
import { GET_PAYMENT_METHODS } from '../actionTypes/PaymentMethodsActionTypes';

const contextSelector = (state) => state.context;

export function* getAddressesSaga(action) {
    try {
        const response = yield call(FactorySaga, getCustomerAddresses, action);
        const payload = {
            response: response.data || response.response.data,
        };
        payload.isLoading = false;
        if (response.isSuccess) {
            payload.isSuccess = true;
            payload.data = [];
            payload.defaultAddress = {};
            if (
                response.response.data &&
                response.response.data.data &&
                response.response.data.data.length > 0
            ) {
                payload.data = response.response.data.data;
                for (let i = 0; i < payload.data.length; i += 1) {
                    if (payload.data[i].isDefault) {
                        payload.defaultAddress = payload.data[i];
                    }
                }
            }
            yield all([put({ type: GET_ADDRESSES_SUCCESS, payload })]);
        } else {
            payload.isSuccess = false;
            payload.error = response.response.data;
            yield put({
                type: GET_ADDRESSES_ERROR,
                payload,
            });
        }
    } catch (error) {
        yield put({
            type: GET_ADDRESSES_ERROR,
            payload: {
                isSuccess: false,
                error,
            },
        });
    }
}

function* errorSagaHelper(actionType, payload) {
    yield put({
        type: actionType,
        payload,
    });
}

function* resetQuickScreenEligiblity() {
    const appContext = yield select(contextSelector);
    const enableQuickScreenBanner = get(appContext, 'featureFlags.enableQuickScreenBanner', true);
    if (enableQuickScreenBanner) {
        yield put({ type: RESET_QUICKSCREEN_ELIGIBLITY });
    }
}

function* buildADIDeviceInfoPayload(action, actionName) {
    const devicePayload = { ...action };
    const appContext = yield select(contextSelector);
    const enableADIAccountUpdate = get(appContext, 'featureFlags.enableADIAccountUpdate', true);
    if (enableADIAccountUpdate) {
        const deviceInfo = yield call(getAccertifyDeviceInfo, {
            actionName,
            enableFeatureSpecificFlag: enableADIAccountUpdate,
        });
        if (deviceInfo) {
            devicePayload.payload.params.deviceInfo = deviceInfo;
        }
    }
    return devicePayload;
}

export function* handleADIResponseforAnalytics(result, actionMethod) {
    const appContext = yield select(contextSelector);
    const enableADIAccountUpdate = get(appContext, 'featureFlags.enableADIAccountUpdate', true);
    if (enableADIAccountUpdate) {
        /* eslint-disable no-unused-expressions */
        const adiStatus = result?.response?.headers?.get('x-adi-sts');
        ['A', 'R'].includes(adiStatus)
            ? yield call(logExtendedAnalytics, {
                  result,
                  isTriggerFromLS: false,
                  actionMethod,
              })
            : '';
    }
}

export function* updateAddressesSaga(action) {
    try {
        let actionParam = { ...action };
        actionParam = yield call(buildADIDeviceInfoPayload, action, 'update_addressToProfileCAM');
        const response = yield call(FactorySaga, updateCustomerAddress, actionParam);
        // Handle ADI response for analytics
        yield call(handleADIResponseforAnalytics, response, 'UPDATE_ADDRESS_PROFILE_CAM');
        const payload = {
            response: actionParam.payload,
        };

        payload.isLoading = false;
        payload.requestFrom = 'UPDATE';
        if (response.isSuccess) {
            payload.isSuccess = true;
            yield call(resetQuickScreenEligiblity);
            yield all([
                put({ type: UPDATE_ADDRESSES_SUCCESS, payload }),
                put({ type: GET_ADDRESSES }),
            ]);
        } else {
            payload.isSuccess = false;
            payload.error = response.data || response.response.data;
            yield fork(errorSagaHelper, UPDATE_ADDRESSES_ERROR, payload);
        }
    } catch (error) {
        yield fork(errorSagaHelper, UPDATE_ADDRESSES_ERROR, error);
        yield put(
            ClientSideLoggerAction.logError(
                'update address failed due to JS error',
                'Customer was not able to update the address'
            )
        );
    }
}

export function* postAddressesSaga(action) {
    try {
        let actionParam = { ...action };
        actionParam = yield call(buildADIDeviceInfoPayload, action, 'add_addressToProfileCAM');
        const response = yield call(FactorySaga, postCustomerAddress, actionParam);
        // Handle ADI response for analytics
        yield call(handleADIResponseforAnalytics, response, 'ADD_ADDRESS_PROFILE_CAM');

        const payload = {
            response: response.data || response.response.data,
        };

        payload.isLoading = false;
        payload.requestFrom = 'POST';
        if (response.isSuccess) {
            payload.isSuccess = true;
            yield call(resetQuickScreenEligiblity);
            yield all([
                put({ type: POST_ADDRESSES_SUCCESS, payload }),
                put({ type: GET_ADDRESSES }),
            ]);
        } else {
            payload.isSuccess = false;
            payload.error = response.data || response.response.data;
            yield fork(errorSagaHelper, POST_ADDRESSES_ERROR, payload);
        }
    } catch (error) {
        yield fork(errorSagaHelper, POST_ADDRESSES_ERROR, error);
        yield put(
            ClientSideLoggerAction.logError(
                'save address failed due to JS error',
                'Customer was not able to save the address'
            )
        );
    }
}

export function* deleteAddressesSaga(action) {
    try {
        const response = yield call(FactorySaga, deleteCustomerAddress, action);
        const payload = {
            deleteId: action.payload.addressId,
        };
        payload.isLoading = false;
        payload.requestFrom = action.userAction ? `DELETE_${action.userAction}` : 'DELETE';
        if (response.isSuccess) {
            payload.isSuccess = true;
            if (action.userAction === 'FORCE') {
                yield fork(getPlccBannerDetails, 'FORCED_DELETE_PAYMENT');
                yield all([
                    put({ type: DELETE_ADDRESSES_SUCCESS, payload }),
                    put({ type: GET_ADDRESSES }),
                    put({ type: GET_PAYMENT_METHODS }),
                ]);
            } else {
                yield all([
                    put({ type: DELETE_ADDRESSES_SUCCESS, payload }),
                    put({ type: GET_ADDRESSES }),
                ]);
            }
        } else if (response.status === 202) {
            payload.isSuccess = false;
            payload.error = {
                errorCode: 'SRV_CUSTOMER_ADDRESS_LINKED_WITH_CREDITCARD',
                errorMessage:
                    'The selected address is associated with one or more payment methods. Please choose one of the options below. ',
            };
            payload.creditCards = response.response.data.data;
            yield all([put({ type: DELETE_ADDRESSES_ERROR, payload })]);
        } else {
            payload.isSuccess = false;
            payload.error = response.response.data;
            yield put({
                type: DELETE_ADDRESSES_ERROR,
                payload,
            });
        }
    } catch (error) {
        yield put({
            type: DELETE_ADDRESSES_ERROR,
            payload: {
                isSuccess: false,
                error,
            },
        });
    }
}

export function* deleteAddressesAndCreateSaga(action) {
    try {
        const modifyAction = action;
        modifyAction.userAction = 'CREATE';
        yield put({ type: DELETE_ADDRESSES_LOADING });
        const response = yield call(FactorySaga, deleteAddressWithCreateUpdate, modifyAction);
        const payload = {
            deleteId: modifyAction.payload.addressId,
        };
        payload.isLoading = false;
        payload.requestFrom = 'DELETE_CREATE';
        if (response.isSuccess) {
            payload.isSuccess = true;
            // payload.newAddress = response.data || action.payload;
            yield all([
                put({ type: DELETE_ADDRESSES_CREATE_SUCCESS, payload }),
                put({ type: GET_ADDRESSES }),
                put({ type: GET_PAYMENT_METHODS }),
            ]);
        } else {
            payload.isSuccess = false;
            payload.error = response.data || response.response.data;
            yield put({
                type: DELETE_ADDRESSES_ERROR,
                payload,
            });
        }
    } catch (error) {
        yield put({
            type: DELETE_ADDRESSES_ERROR,
            payload: {
                isSuccess: false,
                error,
            },
        });
    }
}

export function* deleteAddressesAndUpdateSaga(action) {
    try {
        const modifyAction = action;
        modifyAction.userAction = 'UPDATE';
        modifyAction.payload.isXStandardizationRequired = true;
        delete modifyAction.creditCards;
        delete modifyAction.address;
        yield put({ type: DELETE_ADDRESSES_LOADING });
        const response = yield call(FactorySaga, deleteAddressWithCreateUpdate, modifyAction);
        const payload = {
            deleteId: modifyAction.payload.addressId,
        };
        payload.isLoading = false;
        payload.requestFrom = 'DELETE_UPDATE';
        if (response.isSuccess) {
            payload.isSuccess = true;
            payload.newAddress = modifyAction.payload;
            yield all([
                put({ type: DELETE_ADDRESSES_SUCCESS, payload }),
                put({ type: GET_ADDRESSES }),
                put({ type: GET_PAYMENT_METHODS }),
            ]);
        } else {
            payload.isSuccess = false;
            payload.error = response.data || response.response.data;
            yield put({
                type: DELETE_ADDRESSES_ERROR,
                payload,
            });
        }
    } catch (error) {
        yield put({
            type: DELETE_ADDRESSES_ERROR,
            payload: {
                isSuccess: false,
                error,
            },
        });
    }
}

export function* setDefaultAddressSaga(action) {
    try {
        const response = yield call(FactorySaga, setDefaultAddress, action);
        const payload = {
            response: { addressId: action.payload.id, params: { expressCheckout: true } },
            requestFrom: 'PATCH_FOR_DEFAULT_ADDRESS',
            isLoading: false,
            isSuccess: true,
        };
        if (response.isSuccess) {
            yield call(resetQuickScreenEligiblity);
            yield all([put({ type: UPDATE_ADDRESSES_SUCCESS, payload })]);
        } else {
            payload.isSuccess = false;
            payload.error = response.data || response.response.data;
            yield put({
                type: UPDATE_ADDRESSES_ERROR,
                payload,
            });
        }
    } catch (error) {
        yield put({
            type: UPDATE_ADDRESSES_ERROR,
            payload: {
                isSuccess: false,
                error,
            },
        });
    }
}

function* watchAddressSaga() {
    yield takeLatest(GET_ADDRESSES, getAddressesSaga);
    yield takeLatest(POST_ADDRESSES, postAddressesSaga);
    yield takeLatest(UPDATE_ADDRESSES, updateAddressesSaga);
    yield takeLatest(DELETE_ADDRESSES, deleteAddressesSaga);
    yield takeLatest(DELETE_ADDRESSES_CREATE, deleteAddressesAndCreateSaga);
    yield takeLatest(DELETE_ADDRESSES_UPDATE, deleteAddressesAndUpdateSaga);
    yield takeLatest(SET_DEFAULT_ADDRESS, setDefaultAddressSaga);
}

watchAddressSaga.sagaName = 'addressSaga';
export default watchAddressSaga;
